/**
 * Typography Control Group Component
 *
 * A reusable typography control group that composes multiple typography-related controls
 * into a cohesive group with optional reset functionality.
 *
 * @package RedfoxThankYou
 * @since 1.0.0
 */

import './style.scss';

import { PanelBody, ResetButton } from '@redfox-thank-you/components';
import {
	BaseControl,
	ButtonGroup,
	ColorPicker,
} from '@redfox-thank-you/controls';
import { __experimentalUnitControl as UnitControl } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import { __ } from '@wordpress/i18n';
import { alignCenter, alignLeft, alignRight } from '@wordpress/icons';
import React from 'react';

// Use common responsive values utility for inheritance
import { deviceTypeToBreakpoint,getResponsiveValues } from '../common';
import { DEFAULT_ENABLED_CONTROLS, TYPOGRAPHY_UNITS } from './constants';
import {
	FontFamilyControl,
	FontStyleControl,
	FontWeightControl,
	LetterSpacingControl,
	TextTransformControl,
	WordSpacingControl,
} from './premium';
import {
	TypographyControlProps,
	TypographyValues,
} from './types';
import {
	hasAnyTypographyValues,
	resetTypographyAttributes,
	updateTypographyProperty,
} from './utils';

export const TypographyControl: React.FC< TypographyControlProps > = ( {
	attributes,
	setAttributes,
	enabledControls = DEFAULT_ENABLED_CONTROLS,
	responsive = true,
	className = '',
	fontFamilyOptions: _fontFamilyOptions,
	title = __( 'Typography', 'redfox-thank-you' ),
	initialOpen = false,
	showIndividualResets = true,
	showGroupReset = true,
} ) => {
	// Merge default enabled controls with provided ones
	const controls = { ...DEFAULT_ENABLED_CONTROLS, ...enabledControls };

	// Get WordPress editor device type directly
	const deviceType = useSelect( ( select ) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getDeviceType?.() ?? 'Desktop';
	}, [] );

	const activeBreakpoint = deviceTypeToBreakpoint( deviceType );

	// Determine if we should use responsive mode
	const useResponsive = responsive && controls.responsive;

	/**
	 * Get current typography values for the active breakpoint
	 */
	const getCurrentTypographyValues = () => {
		return getResponsiveValues<TypographyValues>(
			attributes,
			activeBreakpoint,
		);
	};

	/**
	 * Handle attribute updates for different control types
	 */
	const updateAttribute =
		( key: string ) =>
			( value: string | number | ( string | number )[] | undefined ) => {
			// Handle different control value types
				let stringValue = '';
				if ( value !== undefined && value !== null ) {
				// Convert to string if it's a number or array (for SelectControl compatibility)
					if ( Array.isArray( value ) ) {
						stringValue = value[ 0 ]?.toString() ?? '';
					} else {
						stringValue = value.toString();
					}
				}

				// Always update responsive typography values
				const updateData = updateTypographyProperty(
					attributes,
				key as keyof TypographyValues,
				stringValue,
				activeBreakpoint,
				);
				setAttributes( updateData );
			};

	/**
	 * Reset all typography attributes
	 */
	const resetAllTypography = () => {
		const resetData = resetTypographyAttributes();
		setAttributes( resetData );
	};

	/**
	 * Reset individual typography property
	 */
	const resetTypographyProperty = ( property: keyof TypographyValues ) => {
		const updateData = updateTypographyProperty(
			attributes,
			property,
			'',
			activeBreakpoint,
		);
		setAttributes( updateData );
	};

	/**
	 * Check if any typography attributes are set
	 */
	const hasTypographyValues = () => {
		return hasAnyTypographyValues( attributes );
	};

	/**
	 * Check if a specific typography property has a value
	 */
	const hasTypographyValue = (
		property: keyof TypographyValues,
	): boolean => {
		const values = getCurrentTypographyValues();
		const value = values[ property ];
		return !! ( value && value.toString().trim() );
	};

	/**
	 * Render header actions (reset button)
	 */
	const renderHeaderActions = () => {
		return (
			<ResetButton
				onReset={ resetAllTypography }
				show={ showGroupReset && hasTypographyValues() }
				label={ __( 'Reset Typography', 'redfox-thank-you' ) }
				className="redfox-thank-you-typography-control__reset"
			/>
		);
	};

	// Get current values for the active breakpoint
	const currentValues = getCurrentTypographyValues();

	// Create common props for premium controls
	const premiumControlProps = {
		currentValues,
		updateAttribute,
		resetTypographyProperty,
		hasTypographyValue,
		showIndividualResets,
	};

	return (
		<PanelBody
			title={ title }
			initialOpen={ initialOpen }
			className={ `redfox-thank-you-typography-control ${ className }` }
			showResponsive={ useResponsive }
			responsiveProps={ {
				showLabels: false,
			} }
			headerActions={ renderHeaderActions() }
			data-testid="typography-control"
		>
			{ /* Font Family */ }
			{ controls.fontFamily && (
				<FontFamilyControl { ...premiumControlProps } />
			) }

			{ /* Font Weight */ }
			{ controls.fontWeight && (
				<FontWeightControl { ...premiumControlProps } />
			) }
			{ /* Text Color */ }
			{ controls.color && (
				<BaseControl
					id="typography-color"
					label={ __( 'Text Color', 'redfox-thank-you' ) }
					help={ __(
						'Choose a color for the text.',
						'redfox-thank-you',
					) }
					inline
					showResetButton={ showIndividualResets }
					onReset={ () => resetTypographyProperty( 'color' ) }
					hasValue={ hasTypographyValue( 'color' ) }
					resetLabel={ __( 'Reset text color', 'redfox-thank-you' ) }
				>
					<ColorPicker
						value={ currentValues.color }
						onChange={ updateAttribute( 'color' ) }
					/>
				</BaseControl>
			) }

			{ /* Font Size */ }
			{ controls.fontSize && (
				<BaseControl
					id="typography-font-size"
					label={ __( 'Font Size', 'redfox-thank-you' ) }
					help={ __(
						'Set the font size with custom units.',
						'redfox-thank-you',
					) }
					className="redfox-thank-you-base-control--half-width"
					inline
					showResetButton={ showIndividualResets }
					onReset={ () => resetTypographyProperty( 'fontSize' ) }
					hasValue={ hasTypographyValue( 'fontSize' ) }
					resetLabel={ __( 'Reset font size', 'redfox-thank-you' ) }
				>
					<UnitControl
						value={ currentValues.fontSize }
						onChange={ updateAttribute( 'fontSize' ) }
						units={ TYPOGRAPHY_UNITS }
						size="__unstable-large"
						__next40pxDefaultSize
					/>
				</BaseControl>
			) }

			{ /* Line Height */ }
			{ controls.lineHeight && (
				<BaseControl
					id="typography-line-height"
					label={ __( 'Line Height', 'redfox-thank-you' ) }
					help={ __(
						'Set the spacing between lines of text.',
						'redfox-thank-you',
					) }
					className="redfox-thank-you-base-control--half-width"
					inline
					showResetButton={ showIndividualResets }
					onReset={ () => resetTypographyProperty( 'lineHeight' ) }
					hasValue={ hasTypographyValue( 'lineHeight' ) }
					resetLabel={ __( 'Reset line height', 'redfox-thank-you' ) }
				>
					<UnitControl
						value={ currentValues.lineHeight }
						onChange={ updateAttribute( 'lineHeight' ) }
						units={ [
							{ value: '', label: '-' },
							{ value: 'px', label: 'px' },
							{ value: 'em', label: 'em' },
							{ value: 'rem', label: 'rem' },
						] }
						min={ 0 }
						step={ 0.1 }
						size="__unstable-large"
						__next40pxDefaultSize
					/>
				</BaseControl>
			) }

			{ /* Letter Spacing */ }
			{ controls.letterSpacing && (
				<LetterSpacingControl { ...premiumControlProps } />
			) }

			{ /* Word Spacing */ }
			{ controls.wordSpacing && (
				<WordSpacingControl { ...premiumControlProps } />
			) }

			{ /* Font Style */ }
			{ controls.fontStyle && (
				<FontStyleControl { ...premiumControlProps } />
			) }

			{ /* Text Transform */ }
			{ controls.textTransform && (
				<TextTransformControl { ...premiumControlProps } />
			) }

			{ /* Text Alignment */ }
			{ controls.textAlign && (
				<BaseControl
					id="typography-text-align"
					label={ __( 'Text Alignment', 'redfox-thank-you' ) }
					help={ __(
						'Control the horizontal alignment of text.',
						'redfox-thank-you',
					) }
					className="redfox-thank-you-base-control--half-width"
					inline
					showResetButton={ showIndividualResets }
					onReset={ () => resetTypographyProperty( 'textAlign' ) }
					hasValue={ hasTypographyValue( 'textAlign' ) }
					resetLabel={ __(
						'Reset text alignment',
						'redfox-thank-you',
					) }
				>
					<ButtonGroup
						value={ currentValues.textAlign }
						onChange={ updateAttribute( 'textAlign' ) }
						options={ [
							{
								label: __( 'Align Left', 'redfox-thank-you' ),
								value: 'left',
								icon: alignLeft,
							},
							{
								label: __( 'Align Center', 'redfox-thank-you' ),
								value: 'center',
								icon: alignCenter,
							},
							{
								label: __( 'Align Right', 'redfox-thank-you' ),
								value: 'right',
								icon: alignRight,
							},
						] }
						allowDeselect={ true }
					/>
				</BaseControl>
			) }
		</PanelBody>
	);
};
